import React, { useRef, useState } from 'react';
import {
  Container, Box, Typography, TextField, ToggleButtonGroup, ToggleButton, Button, Alert, CircularProgress, Stack,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../store/cart';
import { useLang } from '../i18n/LanguageProvider';
import { placeOrder } from '../lib/orders';
import { DEMO_TENANT_ID, supabase } from '../supabase';
import { TENANT_ID } from '../config';
import { useAuth } from '../context/AuthContext';

const fmt = (n: number) => `${n.toFixed(2).replace('.', ',')} EUR`;

export const Checkout: React.FC = () => {
  const { lines, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useLang();

  const tableFromUrl = params.get('table') || '';
  const initialFulfillment: 'delivery' | 'pickup' | 'dine_in' = tableFromUrl ? 'dine_in' : 'delivery';

  const { profile, isAuthenticated } = useAuth();

  const [fulfillment, setFulfillment] = useState<'delivery' | 'pickup' | 'dine_in'>(initialFulfillment);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [table, setTable] = useState(tableFromUrl);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-order email verification (only used when not signed in). Mirrors
  // Sushiki's `OrderOTPModal` flow but on email: at checkout we ask for the
  // email, send a 6-digit code via Supabase Auth, verify, then place the
  // order. Verifying signs the customer in silently — they don't have to
  // do a separate /auth step. This is "verify at checkout", not "log in
  // first."
  const [otpPhase, setOtpPhase] = useState<'idle' | 'sent' | 'verifying'>('idle');
  const [otpCode, setOtpCode] = useState<string[]>(Array(6).fill(''));
  const [otpError, setOtpError] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  const needsEmailVerification = !isAuthenticated;

  // Pre-fill from the verified auth profile. The phone is locked to the
  // verified number (read-only) so we can trust it for delivery contact.
  React.useEffect(() => {
    if (profile) {
      if (!name && profile.name) setName(profile.name);
      if (profile.phone) setPhone(profile.phone);
      if (!address && profile.street) setAddress(profile.street);
    }
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (lines.length === 0 && !submitting) navigate('/');
  }, [lines, submitting, navigate]);

  /** Place the order. If `token` is provided, use the guest checkout RPC
   *  (place_customer_order_with_email_otp); otherwise use the signed-in
   *  customer path (place_customer_order, reads auth.uid()). */
  const actuallyPlaceOrder = async (token?: string) => {
    const placed = await placeOrder({
      tenantId: DEMO_TENANT_ID,
      customerName: name,
      customerPhone: phone,
      customerEmail: email.trim() || undefined,
      channel: fulfillment === 'dine_in' ? 'qr_table' : 'website',
      fulfillment,
      tableNumber: fulfillment === 'dine_in' ? table : undefined,
      deliveryAddress: fulfillment === 'delivery' ? address : undefined,
      notes: notes || undefined,
      lines,
      verificationToken: token,
    });
    clear();
    navigate(`/order/${placed.id}`);
  };

  /** Pull the actual error message out of a Supabase Edge Function
   *  FunctionsHttpError so the customer sees the real reason. */
  const extractFnError = async (err: any, fallback: string): Promise<string> => {
    try {
      const ctx = err?.context;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        if (body?.error) return body.error;
      }
    } catch { /* fall through */ }
    return err?.message || fallback;
  };

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      // Already signed in → place the order directly.
      if (isAuthenticated) {
        await actuallyPlaceOrder();
        return;
      }

      // Guest checkout — ask our edge function (uses Resend / our own
      // delivery channel, NOT Supabase Auth, so no project-wide email
      // rate limit) to send a 6-digit verification code.
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setError('Please enter a valid email so we can verify your order.');
        setSubmitting(false);
        return;
      }
      setOtpSending(true);
      const { data: sendResult, error: sendErr } = await supabase.functions.invoke('send-customer-otp', {
        body: { email: email.trim(), tenantId: TENANT_ID },
      });
      if (sendErr || (sendResult && sendResult.error)) {
        const msg = await extractFnError(sendErr, sendResult?.error || 'Could not send the code.');
        throw new Error(msg);
      }
      setOtpPhase('sent');
      setOtpCode(Array(6).fill(''));
      setTimeout(() => otpRefs.current[0]?.focus(), 150);
    } catch (e: any) {
      console.error('[CHECKOUT] submit failed:', e);
      setError(e?.message || t('checkout.failed'));
    } finally {
      setSubmitting(false);
      setOtpSending(false);
    }
  };

  /** Confirm the 6-digit code via our edge function, then place the
   *  order with the returned verification_token. */
  const verifyAndPlace = async (code: string) => {
    setOtpPhase('verifying');
    setOtpError('');
    try {
      const { data: verifyResult, error: verifyErr } = await supabase.functions.invoke('verify-customer-otp', {
        body: { email: email.trim(), code, tenantId: TENANT_ID },
      });
      if (verifyErr || (verifyResult && verifyResult.error)) {
        const msg = await extractFnError(verifyErr, verifyResult?.error || 'Verification failed.');
        throw new Error(msg);
      }
      const token = verifyResult?.verification_token;
      if (!token) throw new Error('No verification token returned');
      await actuallyPlaceOrder(token);
    } catch (e: any) {
      console.error('[CHECKOUT] verify failed:', e);
      const msg = String(e?.message || '').toLowerCase();
      if (msg.includes('expired'))      setOtpError('Code expired. Tap Resend.');
      else if (msg.includes('invalid')) setOtpError('Invalid code. Try again.');
      else if (msg.includes('too many'))setOtpError('Too many attempts. Request a new code.');
      else                              setOtpError(e?.message || 'Verification failed.');
      setOtpCode(Array(6).fill(''));
      otpRefs.current[0]?.focus();
      setOtpPhase('sent');
    }
  };

  const handleOtpChange = (i: number, raw: string) => {
    let v = raw.length > 1 ? raw.slice(-1) : raw;
    if (v && !/^\d$/.test(v)) return;
    const next = [...otpCode];
    next[i] = v;
    setOtpCode(next);
    if (v && i < 5) otpRefs.current[i + 1]?.focus();
    if (v && i === 5 && next.every((d) => d !== '')) verifyAndPlace(next.join(''));
  };

  const handleOtpKey = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const resendCode = async () => {
    setOtpError('');
    setOtpSending(true);
    try {
      const { data: sendResult, error: sendErr } = await supabase.functions.invoke('send-customer-otp', {
        body: { email: email.trim(), tenantId: TENANT_ID },
      });
      if (sendErr || (sendResult && sendResult.error)) {
        const msg = await extractFnError(sendErr, sendResult?.error || 'Failed to resend code.');
        throw new Error(msg);
      }
      setOtpCode(Array(6).fill(''));
      otpRefs.current[0]?.focus();
    } catch (e: any) {
      setOtpError(e?.message || 'Failed to resend code.');
    } finally {
      setOtpSending(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 8 }}>
      <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#fff', mb: 0.5 }}>
        {t('checkout.title')}
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>{fmt(subtotal)} · {lines.length} items</Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ToggleButtonGroup
          fullWidth
          exclusive
          value={fulfillment}
          onChange={(_, v) => v && setFulfillment(v)}
          sx={{
            '& .MuiToggleButton-root': {
              color: 'rgba(255,255,255,0.7)',
              borderColor: 'rgba(0,200,150,0.3)',
              fontWeight: 600,
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
                color: '#0A2540',
              },
            },
          }}
        >
          <ToggleButton value="delivery">{t('checkout.fulfillment.delivery')}</ToggleButton>
          <ToggleButton value="pickup">{t('checkout.fulfillment.pickup')}</ToggleButton>
          <ToggleButton value="dine_in">{t('checkout.fulfillment.dine_in')}</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          fullWidth
          label={t('checkout.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={inputSx}
          required
        />
        <TextField
          fullWidth
          label={t('checkout.phone')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          sx={inputSx}
          required
          type="tel"
        />

        {fulfillment === 'delivery' && (
          <TextField
            fullWidth
            label={t('checkout.address')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            sx={inputSx}
            multiline
            minRows={2}
            required
          />
        )}

        {fulfillment === 'dine_in' && (
          <TextField
            fullWidth
            label={t('checkout.table')}
            value={table}
            onChange={(e) => setTable(e.target.value)}
            sx={inputSx}
            required
          />
        )}

        {needsEmailVerification && (
          <TextField
            fullWidth
            label="Email (for order confirmation + receipt)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={inputSx}
            required
            type="email"
            autoComplete="email"
            helperText="We’ll email you a 6-digit code to verify this order. No password needed."
          />
        )}

        <TextField
          fullWidth
          label={t('checkout.notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={inputSx}
          multiline
          minRows={2}
        />

        {/* Email OTP screen — slides in after sending. */}
        {otpPhase !== 'idle' && (
          <Box
            sx={{
              backgroundColor: 'rgba(0,200,150,0.08)',
              border: '1px solid rgba(0,200,150,0.3)',
              borderRadius: 2.5,
              p: 2.5,
              mt: 1,
            }}
          >
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '15px', mb: 0.5 }}>
              Enter the 6-digit code
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', mb: 2 }}>
              We sent a code to <strong style={{ color: '#00C896' }}>{email}</strong>. Enter it below to place your order.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 1.5 }}>
              {otpCode.map((digit, i) => (
                <Box
                  key={i}
                  component="input"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  ref={(el: HTMLInputElement) => { otpRefs.current[i] = el; }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => handleOtpKey(i, e)}
                  disabled={otpPhase === 'verifying'}
                  sx={{
                    width: 42, height: 52, textAlign: 'center', fontSize: '1.3rem', fontWeight: 700,
                    borderRadius: '8px', border: `2px solid ${digit ? '#00C896' : 'rgba(255,255,255,0.2)'}`,
                    background: 'rgba(255,255,255,0.06)', color: '#fff', outline: 'none',
                    transition: 'border-color 0.2s',
                    '&:focus': { borderColor: '#00C896' },
                  }}
                />
              ))}
            </Stack>
            {otpError && (
              <Typography sx={{ color: '#ef5350', fontSize: '12px', textAlign: 'center', mb: 1 }}>{otpError}</Typography>
            )}
            {otpPhase === 'verifying' && (
              <Typography sx={{ color: '#00C896', fontSize: '12px', textAlign: 'center', mb: 1 }}>
                Verifying + placing your order…
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                onClick={() => { setOtpPhase('idle'); setOtpError(''); }}
                sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '12px' }}
              >
                ← Change email
              </Button>
              <Button
                onClick={resendCode}
                disabled={otpSending}
                sx={{ color: '#00C896', textTransform: 'none', fontSize: '12px' }}
              >
                {otpSending ? 'Sending…' : 'Resend code'}
              </Button>
            </Box>
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        <Button
          variant="contained"
          size="large"
          disabled={
            submitting ||
            otpPhase !== 'idle' ||
            !name ||
            !phone ||
            (needsEmailVerification && !email) ||
            (fulfillment === 'delivery' && !address) ||
            (fulfillment === 'dine_in' && !table)
          }
          onClick={submit}
          sx={{
            py: 1.5,
            mt: 1,
            background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
            color: '#0A2540',
            fontWeight: 700,
            fontSize: '15px',
            '&:hover': { background: 'linear-gradient(135deg, #1AD4A6 0%, #00B387 100%)' },
            '&.Mui-disabled': { background: 'rgba(0,200,150,0.3)', color: 'rgba(10,37,64,0.5)' },
          }}
        >
          {submitting || otpSending ? (
            <>
              <CircularProgress size={20} sx={{ color: '#0A2540', mr: 1 }} />
              {needsEmailVerification ? 'Sending code…' : t('checkout.placing')}
            </>
          ) : needsEmailVerification ? (
            `Verify email & place order · ${fmt(subtotal)}`
          ) : (
            `${t('checkout.place_order')} · ${fmt(subtotal)}`
          )}
        </Button>
      </Box>
    </Container>
  );
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(0,200,150,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(0,200,150,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#00C896' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#00C896' },
};
